import type {
  MobileFrontEndBubbleMessageView,
  MobileFrontEndCardView,
  MobileFrontEndGalleryView,
  MobileFrontEndMapView,
  MobileFrontEndTableView,
  MobileFrontEndTimelineView,
  MobileFrontEndViewConfig,
  MobileUiConfigArtifact,
} from '@/shared/mobile-service/api/cors-mobile-api';
import {
  getArtifactViewDef,
  isBubbleView,
  isCardView,
  isGalleryView,
  isMapView,
  isTableView,
  isTimelineView,
} from './view-registry';

/**
 * Accessors for the ``front_end.views`` discriminated union. The narrowing
 * guards live in ``view-registry.ts`` alongside the registry entries that use
 * them, so a view kind is described in exactly one place.
 */

/** Return the ``table`` view from ``front_end.views``, if present. */
export function artifactTableView(
  artifact: MobileUiConfigArtifact | undefined,
): MobileFrontEndTableView | undefined {
  return artifact?.front_end?.views?.find(isTableView);
}

/** Return the ``messages`` (bubble) view from ``front_end.views``, if present. */
export function artifactBubbleView(
  artifact: MobileUiConfigArtifact | undefined,
): MobileFrontEndBubbleMessageView | undefined {
  return artifact?.front_end?.views?.find(isBubbleView);
}

/** Return the ``map`` view from ``front_end.views``, if present. */
export function artifactMapView(
  artifact: MobileUiConfigArtifact | undefined,
): MobileFrontEndMapView | undefined {
  return artifact?.front_end?.views?.find(isMapView);
}

/** Return the ``gallery`` view from ``front_end.views``, if present. */
export function artifactGalleryView(
  artifact: MobileUiConfigArtifact | undefined,
): MobileFrontEndGalleryView | undefined {
  return artifact?.front_end?.views?.find(isGalleryView);
}

/** Return the ``timeline`` view from ``front_end.views``, if present. */
export function artifactTimelineView(
  artifact: MobileUiConfigArtifact | undefined,
): MobileFrontEndTimelineView | undefined {
  return artifact?.front_end?.views?.find(isTimelineView);
}

/** Return the ``cards`` view from ``front_end.views``, if present. */
export function artifactCardView(
  artifact: MobileUiConfigArtifact | undefined,
): MobileFrontEndCardView | undefined {
  return artifact?.front_end?.views?.find(isCardView);
}

/** Prefer curated table-view ``title``, then artifact.title, then table name / type. */
export function artifactDisplayName(
  artifact: MobileUiConfigArtifact | undefined,
  fallback?: string | null,
): string {
  const tableTitle = artifactTableView(artifact)?.title?.trim();
  const title = tableTitle || artifact?.title?.trim();
  if (title) return title;
  if (artifact?.table_name) return artifact.table_name;
  return fallback?.trim() || artifact?.graphql_type || 'Artifact';
}

/**
 * Humanize a view name for the View selector. Prefers the registered label so
 * the selector and the registry can't disagree, and falls back to title-casing
 * for a view kind the schema declares ahead of the frontend.
 */
export function formatViewLabel(
  view: Pick<MobileFrontEndViewConfig, 'name'>,
  t: (key: string) => string,
): string {
  const name = view.name?.trim() || 'view';
  const registered = getArtifactViewDef(name)?.labelKey;
  if (registered) return t(registered);
  return name.charAt(0).toUpperCase() + name.slice(1);
}
